import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useGetSkillPathwayCourseStartLink from 'bundles/skills-common/shared/verified-skills/hooks/useGetSkillPathwayCourseStartLink';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

import _t from 'i18n!nls/course-cards';

const SkillPathwayTakeAssessmentButton = ({ courseId }: { courseId: string }) => {
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const track = useTracker();
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const [isNavigating, setIsNavigating] = React.useState(false);

  // Find the matching skill area for the course
  const matchingSkillArea = badgeMetadata?.skillPathway?.skillAreasWithUserData?.find(
    (skillAreaWithUserData) => skillAreaWithUserData.skillArea.course.id === courseId
  );

  const courseSlug = matchingSkillArea?.skillArea?.course?.slug || '';

  const { fetchSkillPathwayCourseFirstIncompleteAssessmentLink, fetchSkillPathwayCourseStartLink } =
    useGetSkillPathwayCourseStartLink(courseId, courseSlug);

  if (!matchingSkillArea?.skillArea?.course) {
    return null;
  }

  const onClick = async () => {
    track('click_button', {
      button: {
        name: 'skill_pathway_skill_area_take_assessment',
      },
      enterprise: {
        ...(thirdPartyOrganization?.slug ? { organizationSlug: thirdPartyOrganization?.slug } : {}),
        ...(program?.slug ? { programSlug: program?.slug } : {}),
      },
      product: {
        id: courseId,
        type: 'course',
      },
    });

    setIsNavigating(true);

    try {
      // Fetch the first incomplete assessment link, or the last one if all are completed
      const assessmentLink = await fetchSkillPathwayCourseFirstIncompleteAssessmentLink();

      if (assessmentLink) {
        setLocation(assessmentLink);
      } else {
        // Fall back to course start link if no assessment found
        const skillPathwayCourseStartLink = await fetchSkillPathwayCourseStartLink();
        setLocation(skillPathwayCourseStartLink);
      }
    } catch {
      // If fetching fails, fall back to the course home page
      setLocation(`/learn/${courseSlug}/home/welcome`);
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <Button variant="ghost" size="small" style={{ marginTop: '12px' }} onClick={onClick} loading={isNavigating}>
      {_t('Take assessment')}
    </Button>
  );
};

export default SkillPathwayTakeAssessmentButton;
